import { type ReactNode, useCallback, useRef, useState } from "react";
import { useT } from "../i18n/context";
import type { BackAction } from "./TopBar";
import { useModalFocus } from "./useModalFocus";

/**
 * Leaving a match in progress is not undoable (review 0024 decision 171): a bot takes the seat and
 * the match goes on without you. The lobby and the end of a match ask nothing.
 */
export function useLeaveConfirm(
  back: BackAction | null,
  playing: boolean,
): { back: BackAction | null; dialog: ReactNode } {
  const { t } = useT();
  const [asking, setAsking] = useState(false);
  const stay = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const keep = useCallback(() => setAsking(false), []);
  // The way back takes the focus (WCAG 3.3.4): Enter and Escape keep you at the table, and Tab
  // stays in the dialog, so the cards under the veil cannot be played meanwhile (0027 G2).
  useModalFocus(box, asking, { initial: stay, onEscape: keep });
  if (back === null || !playing) return { back, dialog: null };
  const guarded = { label: back.label, onClick: () => setAsking(true) };
  const dialog = asking ? (
    <div
      ref={box}
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
      className="absolute inset-0 z-40 flex items-center justify-center bg-ground/55 backdrop-blur-[3px]"
    >
      <div className="flex w-[min(360px,88%)] flex-col gap-3 rounded-[22px] border-[1.5px] border-edge bg-panel-strong px-6 py-5 shadow-panel">
        <h2 id="leave-title" className="m-0 text-[17px] font-extrabold">
          {t("table.leave.title")}
        </h2>
        <p className="m-0 text-[14px] text-ink/80">{t("table.leave.body")}</p>
        <div className="mt-1 grid grid-cols-2 gap-3">
          <button
            type="button"
            ref={stay}
            className="btn-gold h-11 rounded-pill font-extrabold"
            onClick={keep}
          >
            {t("table.leave.stay")}
          </button>
          <button
            type="button"
            className="h-11 rounded-pill border-[1.5px] border-danger-soft/70 font-bold text-danger-soft hover:bg-danger/20"
            onClick={back.onClick}
          >
            {t("table.leave.go")}
          </button>
        </div>
      </div>
    </div>
  ) : null;
  return { back: guarded, dialog };
}
